<template>
  <div class="school-chart">
    <v-card>
      <v-flex class="hidden">
        {{ startChart }}
        {{ isMobile }}
      </v-flex>
      <v-card-title>
        <v-layout column wrap>
          <v-layout
            wrap
            row
            class="dialog-content"
          >
            <v-flex
              class="indicators"
            >
              <v-flex class="indicators-title">
                {{ $t('Indicators') }}
              </v-flex>
              <v-select
                v-model="indicator"
                :items="indicatorItems"
                hide-details
                return-object
                outline
                class="indicator-select hidden-md-and-up"
                @change="updateSchoolData({indicator: indicator.indicator}, chartType).then(() => initChart(indicator));"
              >
                  <template v-slot:selection="{ item, index }">
                    {{ $t(item.indicator) }}
                  </template>
                  <template v-slot:item="{ item, index }">
                    {{ $t(item.indicator) }}
                  </template>
              </v-select>
              <ul class="indicators-items hidden-sm-and-down">
                <li v-for="(indicator, index) in indicators" :key="index" :class="{active:index == indicatorSelected}">
                  <div class="indicators-list-style">
                    <a href="#" @click.stop.prevent="updateSchoolData(indicator, chartType).then(() => initChart(indicator)); indicatorSelected = index;">
                      {{ $t(indicator.indicator.trim()) }}
                      <v-tooltip
                        top
                        content-class="school-tooltip"
                        close-delay="1000"
                        color="#3a5a86"
                        v-if="toolTipByName(indicator.indicator)">
                        <template v-slot:activator="{ on }">
                          <v-icon class="tooltip-icon" v-on="on">help</v-icon>
                        </template>
                        <div v-html="toolTipByName(indicator.indicator)"></div>
                      </v-tooltip>
                    </a>
                  </div>
                </li>
              </ul>
            </v-flex>
            <v-flex
              :class="[chartType, 'chart']"
            >
              <v-flex class="chart-title hidden-sm-and-down">
                {{ chartTitle }}
              </v-flex>
              <v-flex class="chart-actions text-xs-right hidden-sm-and-down">
                <v-btn
                  class="email-button"
                  flat
                  @click.stop="openEmailModal()"
                >
                  email
                  <i class="icon"></i>
                </v-btn>
                <v-btn
                  flat
                  class="print-button"
                  @click="print()"
                >
                  {{ $t('Print') }}
                  <i class="icon"></i>
                </v-btn>
              </v-flex>

              <SchoolSendMailModal ref="emailModal"/>

              <canvas
                v-if="isMobile"
                id="compareChart"
                height="250px" />
              <canvas
                v-else
                id="compareChart"
                height="200px"/>

              <v-layout
                row
                wrap
                class="legend"
                v-if="isMobile && chartType == 'compare' || !isMobile"
                >
                <v-flex v-if="averageLabel">
                  <v-layout col wrap>
                    <v-flex class="legend-square">
                      <div
                        :style="{
                          'border': '1px dashed rgb(97, 208, 149)',
                          'width': '20px',
                          'height': '1px'
                          }"
                      ></div>
                    </v-flex>
                    <v-flex class="legend-school-name">
                      {{ $t('All Schools Average') }}
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-layout>
        <v-flex
          xs12
          class="school-table-note"
          v-if="chartType === 'single' && singleSchool.province == 'on'">
          Note: Most provinces paused their testing programs for two years as part of their policy response to Covid-19 so we will not be able to fill in the historical results for 2020 or 2021.
        </v-flex>
        <v-flex
          xs12
          class="school-table-note"
          v-if="singleSchool && singleSchool.province === 'bc' && !isSecondary"
        >Note: In the 2021/22 school year, the BC government replaced the Reading and Writing FSAs with a Literacy FSA as part of changes to align with the new curriculum. As a result, the two FSAs ‘Reading’ and ‘Writing’ are replaced in 2022 by a single FSA, ‘Literacy’. The results for the new FSA, ‘Literacy’ are not comparable to earlier results for ‘Reading’ and ‘Writing’.
        </v-flex>
        <v-flex
          xs12
          class="school-table-note"
          v-if="singleSchool && singleSchool.province === 'ab'"
        >Note: Most provinces paused their testing programs for two years as part of their policy response to Covid-19 so we will not be able to fill in the historical results for 2020 or 2021. Courses taken per student calculation for 2023 is revised.
        </v-flex>
        <v-flex
          xs12
          class="school-table-note"
          v-if="singleSchool && singleSchool.province === 'qc' && languageIs('en')"
        ><p>Note: Most provinces paused their testing programs for two years as part of their policy response to Covid-19 so we will not be able to fill in the historical results for 2020 or 2021.</p>
        </v-flex>
        <v-flex
          xs12
          class="school-table-note"
          v-if="singleSchool && singleSchool.province === 'qc' && languageIs('fr')"
        ><p>Note: La plupart des provinces ont interrompu leurs programmes d’examens pendant deux ans dans le cadre de leur réponse à la COVID-19. Nous ne serons donc pas en mesure de présenter les résultats pour 2020 ni 2021.</p>
        </v-flex>
      </v-card-title>
    </v-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { Chart } from 'chart.js';
import SchoolSendMailModal from '@/components/SchoolMap/SchoolSendMailModal';
import RepositoryFactory from '@/services/RepositoryFactory';
import { INDICATORS_DATA } from '@/services/Indicators';
const SystemRepository = RepositoryFactory.get('system');
const SchoolService = RepositoryFactory.get("schools");
const StorageService = RepositoryFactory.get('storage');


export default {
  name: 'SchoolChart',
  props: ['chartType', 'schoolId', 'singleSchool', 'averageLabel', 'isSecondary'],
  components: {
    SchoolSendMailModal,
  },
  data() {
    return {
      rules: {
          counter: value => value.length <= 20 || 'Max 20 characters',
        },
      isMobile: false,
      indicatorSelected: 0,
      chart: null,
      chartHelper: null,
      colorSchemes: ["#ffffff"],
      chartTitle: null,
      indicator: {
        indicator: "Overall rating out of 10",
        min: 0,
        max: 10
      },
      indicatorItems: [],
      compareSchoolList: [],
    };
  },
  async mounted() {
    let indicator = {
      indicator: 'Overall rating out of 10',
      min: 0,
      max: 10
    };
    window.addEventListener('resize', this.checkIsMobile);
    this.checkIsMobile();
    await this.updateSchoolData(indicator, this.chartType);
    this.initChart(indicator);
    this.getInitIndicators();
  },
  methods: {
    languageIs(language) {
      return localStorage.getItem('language') === language;
    },
    formatNumber(num) {
      if(num == undefined) {
        return 0;
      }
      return parseFloat(num.toString().replace(',', '.')).toFixed(1);
    },
    round(value, precision) {
      const multiplier = Math.pow(10, precision || 0);
      const rounded = Math.round(value * multiplier) / multiplier;

      return rounded.toFixed(precision);
    },
    openEmailModal() {
      this.$refs.emailModal.openModal();
    },
    print() {
      window.print();
    },
    toolTipByName(name) {
      let provinces = this.$store.state.provinceList;
      let currentMapProvince = this.$store.state.currentMapProvince;
      let currentProvinceValue;
      for (let i = provinces.length -1; i >= 0; i--) {
        if (provinces[i].name === currentMapProvince) {
          currentProvinceValue = provinces[i].value;
        }
      }

      let language = localStorage.getItem('language');
      let langTranslation = language;

      if (currentProvinceValue === 'qc') {
        langTranslation = 'fr';
      }

      let educationLevel = this.singleSchool !== undefined ? this.singleSchool.level : this.$store.state.currentEducationLevel;
      let tooltip;
      if (name) {
        tooltip = SystemRepository.getTooltipByName(this.$t(name), currentProvinceValue, educationLevel, language);
      }

      // Try using translation if null.
      if (!tooltip) {
        tooltip = SystemRepository.getTooltipByName(this.$t(name, langTranslation), currentProvinceValue, educationLevel, language);
      }
      return tooltip || null;
    },
    async getAverageData(province, level, indicator) {
      let averageDataFilter = {
        province: province,
        schoolType: level,
        title: indicator
      };
      /*console.info({
        "element-model": 'School chart',
        "action: ": "Reload average data",
        "request": `Get Average Data: (${averageDataFilter})`,
        time: Math.floor(Date.now() / 1000)
      },"EVENT");*/
      return await SchoolService.getAverageData(averageDataFilter);
    },
    async updateSchoolData(indicator, chartType) {
      let latestYear = StorageService.getItem('latestYear');
      latestYear = JSON.parse(latestYear);

      if (chartType == 'compare') {
        let schools = this.$store.state.compareSchool;
        let schoolsLabel = [];
        let schoolsData = [];
        let singleSchool;
        for (let i = schools.length - 1; i >= 0; i --) {
          /*console.info({
            "element-model": indicator,
            "action: ": "Update compare chart data",
            "request": `Get School By ID: ${schools[i].id}`,
            time: Math.floor(Date.now() / 1000)
          },"EVENT");*/
          singleSchool = SchoolService.getById(schools[i].id, schools[i].level, schools[i].province);
          singleSchool
              .then((res) => {
                let currentIndicator = this.resolveIndicator(res, indicator.indicator);
                if (res['chartData'].hasOwnProperty(currentIndicator)) {
                  let latestYearValue = latestYear[res.province + '_' + res.schoolType];
                  // let availibleYears = Object.keys(res['chartData'][currentIndicator]).map((year) => {
                  //   return parseFloat(year);
                  // });
                  // let availibleYear = Math.max(...availibleYears);
                  // if (availibleYears.indexOf(latestYearValue) === -1) {
                  //   latestYearValue = availibleYear;
                  // }
                  if (res['chartData'][currentIndicator][latestYearValue]) {
                    schoolsData.push({
                      id: res.id,
                      data: res['chartData'][currentIndicator][latestYearValue].replace(/[^\d.-]/g, '')
                    });
                  }
                }

                schoolsLabel.push({
                  id: schools[i].id,
                  label: schools[i].name.substring(0, 20)
                });

              });
        }

        return Promise.all([singleSchool]).then(() => {
          this.$store.commit("SET_SCHOOLS_DATA", schoolsData);
          this.$store.commit("SET_SCHOOLS_LABEL", schoolsLabel);
          let colorSchemes = Chart.colorschemes;
          this.colorSchemes = colorSchemes.tableau.Tableau10;
        });
      }
      else if (chartType === 'single') {
        let schoolsData = [];
        let schoolsLabel = [];
        let schoolValues = this.singleSchool;
        let currentIndicator = this.resolveIndicator(schoolValues, indicator.indicator);

        let data = {};
        if (schoolValues['chartData'].hasOwnProperty(currentIndicator)) {
          data = JSON.parse(JSON.stringify(schoolValues['chartData'][currentIndicator]));
        }

        /*console.info({
          "element-model": indicator,
          "action: ": "Update compare chart data",
          "request": `NONE (getting data from localStorage)`,
          time: Math.floor(Date.now() / 1000)
        },"EVENT");*/
        Object.keys(data).forEach(function(key) {
          let schoolLabel = {
            id: schoolValues.id,
            label: key,
          };
          let schoolData = {
            id: schoolValues.id,
            data: data[key].replace(/[^\d.-]/g, '')
          }
          schoolsLabel.push(schoolLabel);
          schoolsData.push(schoolData);
        });
        this.$store.commit("SET_SCHOOLS_LABEL", schoolsLabel);
        this.$store.commit("SET_SCHOOLS_DATA", schoolsData);
        this.colorSchemes = "#3A5A86";
      }
    },
    resolveIndicator(data, currentIndicator) {
      if (currentIndicator === 'Overall rating out of 10' && !data['chartData'].hasOwnProperty(currentIndicator)) {
        // Try french version.
        let currentIndicatorTranslation = 'Cote globale (sur 10)';
        if (data['chartData'].hasOwnProperty(currentIndicatorTranslation)) {
          return currentIndicatorTranslation;
        }
      }

      return currentIndicator;
    },
    checkIsMobile() {
      this.isMobile = parseInt(document.documentElement.clientWidth) < 960;
    },
    async initChart(indicator) {
      let self = this;
      let ctx = document.getElementById("compareChart").getContext("2d");
      setTimeout(async () => {
        if (this.chart != null) {
          this.chart.destroy();
        }

        let config = {
          type: "horizontalBar"
        };

        if (indicator == null) {
          indicator = {
            indicator: "Overall rating out of 10",
            label: "Overall rating out of 10"
          };
        } else if (typeof indicator === 'string') {
          indicator = {
            indicator,
            label: indicator
          };
        }
        let latestYear = StorageService.getItem('latestYear');
        latestYear = JSON.parse(latestYear);
        let language = localStorage.getItem('language');
        this.chartTitle = this.$t(indicator.indicator);

        if (indicator.indicator === 'Overall rating out of 10' && language === 'fr') {
          this.chartTitle = 'Cote globale (sur 10)';
        }

        let average = null;
        if (this.chartType == 'compare') {
          this.chartTitle += language === 'fr' ? " graphique comparatif" : " Comparison chart";

          let school = await this.$store.state.compareSchool[0];
          let averageData = await this.getAverageData(school.province, school.level, indicator.indicator);
          if (averageData !== undefined && averageData !== null) {
            average = averageData.schoolAvgData[latestYear[school.province + '_' + school.level]];
            // let schoolAvgData = averageData.schoolAvgData;
            // let averageYear = 0;
            // Object.keys(schoolAvgData).forEach(function(key) {
            //   if (
            //       key
            //       && !isNaN(Number(key))
            //       && schoolAvgData[key] !== null
            //       && !isNaN(Number(schoolAvgData[key].replace(',', '.')))) {
            //     if (key > averageYear) {
            //       average = schoolAvgData[key].replace(',', '.');
            //       averageYear = key;
            //     }
            //   }
            // });
          }
        } else {
          let averageData = await this.getAverageData(this.singleSchool.province, this.singleSchool.level, indicator.indicator);
          if(averageData) {
            let schoolAvgData = averageData.schoolAvgData;
            let averageYear = 0;
            Object.keys(schoolAvgData).forEach(function(key) {
              if (
                  key
                  && !isNaN(Number(key))
                  && schoolAvgData[key] !== null
                  && !isNaN(Number(schoolAvgData[key].replace(',', '.')))) {
                if (key > averageYear) {
                  average = schoolAvgData[key].replace(',', '.');
                  averageYear = key;
                }
              }
            });
          }
        }

        // Sorting based on compareSchool array
        let sortingIds = this.$store.state.compareSchool.map(function(item) {
          return item.id;
        });

        let schoolsData = [];
        let schoolsDataValues = this.$store.state.schoolsData;

        schoolsDataValues.sort(function(a, b) {
            return sortingIds.indexOf(a.id) - sortingIds.indexOf(b.id);
        });

        schoolsDataValues.forEach(schoolData => {
          schoolsData.push(schoolData.data);
        });

        let schoolsLabel = [];
        let schoolsLabelData = this.$store.state.schoolsLabel;
        this.compareSchoolList = schoolsLabelData;
        if (this.chartType === 'compare') {
          schoolsLabelData.sort(function(a, b) {
            return sortingIds.indexOf(a.id) - sortingIds.indexOf(b.id);
          });
          schoolsLabelData.forEach((schoolLabel) => {
            schoolsLabel.push(schoolLabel.label);
          });
        } else {
          this.$store.state.schoolsLabel.forEach((schoolLabel) => {
            schoolsLabel.push(schoolLabel.label);
          });

          // Change schools data and labels order.
          schoolsData.reverse();
          schoolsLabel.reverse();
        }

        // let total = 0;
        // for (let i = 0, ln = schoolsData.length; i < ln; i++) {
        //   total += Number.parseFloat(schoolsData[i]);
        // }

        // let ctx = document.getElementById("compareChart").getContext("2d");
        // ctx.clearRect(0, 0, ctx.width, ctx.height);

        var averageText = (parseFloat(average) % 1 === 0 ? parseFloat(average) : this.round(average, 1)) + ' ' + this.$t('All Schools Average').toUpperCase();

         //Create horizontalBar plug-in for ChartJS
        var originalLineDraw = Chart.controllers.horizontalBar.prototype.draw;
        this.chartHelper = null;
        this.chartHelper = Chart.helpers.extend(Chart.controllers.horizontalBar.prototype, {

          draw: function () {
            originalLineDraw.apply(this, arguments);

            var chart = this.chart;
            var ctx = chart.chart.ctx;

            var index = self.formatNumber(chart.config.options.lineAtIndex);
            if (index) {
              var xaxis = chart.scales['x-axis-0'];
              var yaxis = chart.scales['y-axis-0'];

              var x1 = xaxis.getPixelForValue(index);
              var y1 = 0;

              var x2 = xaxis.getPixelForValue(index);
              var y2 = yaxis.height;

              // Set rectangle and corner values
              var rectWidth = averageText.length * 10;
              var rectHeight = 26;
              var cornerRadius = 25;
              ctx.fillStyle = "#B4DBC7";
              ctx.strokeStyle = "#B4DBC7";
              // Set faux rounded corners
              ctx.lineJoin = "round";
              ctx.lineWidth = cornerRadius;

              // Change origin and dimensions to match true size (a stroke makes the shape a bit larger)
              var xPos = Math.min(x1, 350) - rectWidth/2;

              if (xPos < 0) {
                xPos = 20;
              }

              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, 600, 30);
              ctx.fillStyle = "#B4DBC7";
              ctx.strokeRect(xPos, y1 + 15, rectWidth - cornerRadius, rectHeight - cornerRadius);
              ctx.fillRect(xPos, y1 + 15, rectWidth - cornerRadius, rectHeight - cornerRadius);

              if (average) {
                ctx.textAlign = 'start';
                ctx.fillStyle = "#000000";
                ctx.font = "12px Metropolis Light";
                ctx.textBaseline = "middle";
                ctx.fillText(averageText, xPos, y1 + rectHeight/2 + 3);
              }

              // small triangle
              // triangle 1, at left
              ctx.beginPath();
              ctx.fillStyle = "#B4DBC7";
              ctx.moveTo(x1 - 5, y1 + 28); // start at top left corner of canvas
              ctx.lineTo(x1 + 5, y1  + 28);
              ctx.lineTo(x1, y1 + 38); // go to horizontal 100 (x) and vertical 200 (y)
              ctx.fill(); // connect and fill
              ctx.beginPath();


              // Line
              ctx.save();
              ctx.beginPath();
              ctx.lineWidth = 2;
              ctx.fillStyle = "#ffffff";
              ctx.textBaseline = "bottom";
              ctx.moveTo(x1, y1 + 40);
              ctx.setLineDash([5, 15]);
              ctx.strokeStyle = '#B4DBC7';
              ctx.lineTo(x2, y2 + 70);
              ctx.stroke();
              ctx.restore();

              // Default chart color
              ctx.fillStyle = "#3A5A86";
            }
          }
        });

        let colorSchemesInChart = [];

        if (this.chartType == 'single') {
          colorSchemesInChart = this.colorSchemes;
        }
        else {
          schoolsData.forEach((schoolItem, schoolIndex) => {
            colorSchemesInChart.push(this.colorSchemes[schoolIndex % this.colorSchemes.length]);
          });
        }

        let xAxesTicks = {
          beginAtZero: true,
          fontSize: 14,
          callback: function(value) {if (value % 1 === 0) {return value;}}
        };
        // console.log(indicator);
        if (indicator.hasOwnProperty('min')) {
          xAxesTicks['min'] = indicator.min;
        }
        if (indicator.hasOwnProperty('max')) {
          xAxesTicks['max'] = indicator.max;
        }
        this.chart = new Chart(ctx, {
          type: config.type,
          data: {
            labels: schoolsLabel,
            datasets: [
              {
                backgroundColor: colorSchemesInChart,
                data: schoolsData
              }
            ]
          },
          options: {
            lineAtIndex: average,
            legend: { display: false },
            responsive:true,
            scales: {
              xAxes: [
                {
                  ticks: xAxesTicks
                }
              ],
              yAxes: [
                {
                  ticks: {
                    fontSize: 14
                  }
                }
              ]
            },
            layout: {
                padding: {
                    left: 0,
                    right: 40,
                    top: 46,
                    bottom: 0
                }
            },
            title: {
              display: false
            },
            defaults: {
              global: {
                fontFamily: "Metropolis Light"
              }
            },
            plugins: {
              datalabels: {
                formatter: (value) => {
                  return value;
                  //return value !== 0 ?
                  //  value.toFixed(1).toLocaleString( /* ... */ ) :
                  //  ''
                },
                anchor: 'end',
                align: 'end',
                color: '#4a4a4a',
                font: {
                  family: 'Metropolis Light',
                  size: '14'
                }
              }
            }
          }
        });
      }, 500);
    },
    deleteCompareSchool(id) {
      this.$store.dispatch("deleteCompareSchool", id);
      this.$store.commit("DELETE_SCHOOL_DATA", id);
      this.$store.commit("DELETE_SCHOOL_LABEL", id);
      this.initChart();
    },
    getInitIndicators() {
      let vm = this;
      this.getIndicators().then(result => {
        vm.indicatorItems = result;
        if (result) {
          vm.indicator = result[0];
        }
      });
    },
    async getIndicators() {
      let vm = this;
      let top = '';
      let indicators = [];
      let indicatorsArray = [];
      let schools;
      if (this.chartType == 'compare') {
        let promise = await new Promise((res) => {
          res(this.$store.state.compareSchool);
        });
        schools = await promise;
      }
      else if (this.chartType == 'single') {
        schools = [vm.singleSchool];
      }
      schools.forEach(school => {
      // console.log(school);
        for (let chartData in school['chartData']) {
          if (chartData.toLowerCase().indexOf('gender gap') === -1 &&
            chartData.toLowerCase().indexOf('gender difference') === -1 &&
            chartData.toLowerCase() !== 'mathematics' &&
            chartData.toLowerCase().indexOf('écart sexe') === -1 &&
              (chartData.toLowerCase().indexOf('mathématiques') === -1) || chartData.toLowerCase().trim().indexOf('mathématiques (%)') !== -1) {
            indicators[chartData] = INDICATORS_DATA[chartData] || { indicator: chartData };
          }
        }
      });

      for (let key in indicators) {
        if (indicators.hasOwnProperty(key)) {
          if (indicators[key].indicator != 'Overall rating out of 10' && indicators[key].indicator != 'Cote globale (sur 10)') {
            indicatorsArray.push(indicators[key]);
          }
          else {
            top = indicators[key];
          }
        }
      }
      indicatorsArray.unshift(top);
      return indicatorsArray;
    }
  },
  computed: {
    ...mapState({

    }),
    startChart: {
      get: function() {
        return this.$store.state.dialogCompareSchool;
      },
      set: function(value) {
        this.$store.commit("SET_DIALOG_COMPARE_SCHOOL", value);
      }
    },
    isOntarioProvince() {
      return this.$store.state.currentMapProvince === 'Ontario';
    }
  },
  asyncComputed: {
    indicators: {
      lazy: true,
      async get() {
        return await this.getIndicators();
      },
    }
  }
}
</script>
